import {
    animate,
    group,
    query,
    style,
    transition,
    trigger,
} from '@angular/animations';
import { Component, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

import { ChatActivity } from '../../../types/chat-session.types';

const ACTIVITY_LABELS: Record<string, string> = {
    typing: 'Печатает...',
    thinking: 'Думает...',
    fetching: 'Загружает...',
    pending: 'Ожидает...',
};

interface LabelFrame {
    id: number;
    text: string;
}

@Component({
    selector: 'chat-activity',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './chat-activity.component.html',
    styleUrls: ['./chat-activity.component.scss'],
    animations: [
        trigger('labelTransition', [
            transition('* => *', [
                group([
                    query(
                        ':leave',
                        [
                            style({ opacity: 1, transform: 'translateY(0)' }),
                            animate(
                                '220ms ease-out',
                                style({ opacity: 0, transform: 'translateY(-8px)' }),
                            ),
                        ],
                        { optional: true },
                    ),
                    query(
                        ':enter',
                        [
                            style({ opacity: 0, transform: 'translateY(8px)' }),
                            animate(
                                '220ms ease-out',
                                style({ opacity: 1, transform: 'translateY(0)' }),
                            ),
                        ],
                        { optional: true },
                    ),
                ]),
            ]),
        ]),
    ],
})
export class ChatActivityComponent implements OnChanges, OnDestroy {
    @Input() public activity: ChatActivity | null = null;

    /** Текущий кадр лейбла; смена id запускает transition leave/enter. */
    public frames: LabelFrame[] = [];

    private nextFrameId = 0;
    private lastShownTime = 0;
    private pendingLabel: string | null = null;
    private pendingTimeoutId: any = null;

    public ngOnChanges(changes: SimpleChanges): void {
        if (!changes['activity']) {
            return;
        }

        this.syncLabelFrames();
    }

    public ngOnDestroy(): void {
        if (this.pendingTimeoutId !== null) {
            clearTimeout(this.pendingTimeoutId);
        }
    }

    public trackByFrameId(_index: number, frame: LabelFrame): number {
        return frame.id;
    }

    private syncLabelFrames(): void {
        const nextLabel = this.resolveLabel(this.activity);
        const currentLabel = this.frames[this.frames.length - 1]?.text ?? null;

        if (currentLabel === nextLabel) {
            this.pendingLabel = null;
            if (this.pendingTimeoutId !== null) {
                clearTimeout(this.pendingTimeoutId);
                this.pendingTimeoutId = null;
            }
            return;
        }

        const now = Date.now();
        const elapsed = now - this.lastShownTime;
        const minDuration = 500;

        if (elapsed >= minDuration) {
            if (this.pendingTimeoutId !== null) {
                clearTimeout(this.pendingTimeoutId);
                this.pendingTimeoutId = null;
            }
            this.pendingLabel = null;
            this.setLabel(nextLabel);
        } else {
            this.pendingLabel = nextLabel;
            if (this.pendingTimeoutId === null) {
                const delay = minDuration - elapsed;
                this.pendingTimeoutId = setTimeout(() => {
                    this.pendingTimeoutId = null;
                    const targetLabel = this.pendingLabel;
                    this.pendingLabel = null;
                    this.setLabel(targetLabel);
                }, delay);
            }
        }
    }

    private setLabel(label: string | null): void {
        const currentLabel = this.frames[this.frames.length - 1]?.text ?? null;
        if (currentLabel === label) {
            return;
        }

        if (!label) {
            this.frames = [];
        } else {
            this.frames = [{ id: this.nextFrameId++, text: label }];
        }
        this.lastShownTime = Date.now();
    }

    private resolveLabel(activity: ChatActivity | null): string | null {
        if (!activity) {
            return null;
        }

        return activity.label ?? ACTIVITY_LABELS[activity.type] ?? null;
    }
}
